/* ── Tipos de Contratos (espelham os DTOs do backend /api/v1) ────────────
 * Fonte: backend/apps/api/src/features/sales/presentation/http/dto/
 *   - contract.dto.ts            (ContractResponseDto, UpdateContractDto)
 *   - contract-lifecycle.dto.ts  (billing schedule, renewals, dashboard)
 *   - approval-rule.dto.ts       (regras de alçada)
 *
 * R3C: 5 telas de contratos consomem estes tipos via useContractQueries.
 */

/* ── RecurringContract / contratos ── */

export type ContractPeriodicity =
  | 'WEEKLY'
  | 'BIWEEKLY'
  | 'MONTHLY'
  | 'BIMONTHLY'
  | 'QUARTERLY'
  | 'SEMIANNUAL'
  | 'ANNUAL';

export type ContractStatus = 'ACTIVE' | 'SUSPENDED' | 'CANCELLED';

/** Espelha ContractItemResponseDto (item de contrato, com SLA e custo mensal). */
export interface ContractItem {
  id: string;
  productId: string;
  description: string;
  quantity: number;
  unitValue: number;
  slaTarget: string | null;
  monthlyCost: number | null;
}

/** Espelha ContractResponseDto. */
export interface Contract {
  id: string;
  tenantId: string;
  clientId: string;
  startDate: string;
  endDate: string | null;
  periodicity: ContractPeriodicity;
  value: number;
  items: ContractItem[];
  status: ContractStatus;
  lastBillingDate: string | null;
  nextBillingDate: string;
  cancellationReason: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Espelha UpdateContractDto + UpdateContractItemDto para PATCH /contracts/:id. */
export interface ContractUpdateItemInput {
  id?: string;
  productId?: string;
  description?: string;
  quantity?: number;
  unitValue?: number;
  slaTarget?: string | null;
  monthlyCost?: number | null;
}

export interface ContractUpdateInput {
  clientId?: string;
  startDate?: string;
  endDate?: string | null;
  periodicity?: ContractPeriodicity;
  value?: number;
  items?: ContractUpdateItemInput[];
}

/* ── Cronograma de faturamento ── */

export type BillingStatus = 'PENDING' | 'BILLED' | 'PAID' | 'CANCELLED';

/** Espelha ContractBillingScheduleItemDto. Datas chegam serializadas (ISO). */
export interface BillingScheduleItem {
  id: string;
  contractId: string;
  installment: number;
  competence: string;
  dueDate: string;
  amount: number;
  status: string;
  billedAt: string | null;
  paidAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Espelha CreateBillingScheduleItemDto (um item do batch). */
export interface BillingScheduleItemInput {
  installment: number;
  competence: string;
  dueDate: string;
  amount: number;
}

/** Espelha UpdateBillingScheduleItemDto (PATCH /contracts/:id/billing-schedule/:itemId). */
export interface BillingScheduleItemUpdate {
  status?: BillingStatus;
  dueDate?: string;
  competence?: string;
  amount?: number;
}

export interface Paginated<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

/* ── Renovação de contratos ── */

export type RenewalStatus = 'PROPOSED' | 'APPROVED' | 'REJECTED' | 'EXECUTED';

/** Espelha ContractRenewalDto. */
export interface ContractRenewal {
  id: string;
  contractId: string;
  vendorName: string | null;
  indexName: string | null;
  currentValue: number;
  proposedValue: number;
  status: string;
  effectiveDate: string | null;
  requestedById: string | null;
  approvedById: string | null;
  approvedAt: string | null;
  rejectedById: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Espelha ExpiringContractDto. */
export interface ExpiringContract {
  contractId: string;
  clientId: string;
  clientName: string | null;
  value: number;
  endDate: string;
  daysToExpiry: number;
  status: string;
}

/** Espelha RenewalPipelineDto (GET /contracts/renewals/pipeline). */
export interface RenewalPipeline {
  expiringAlerts: ExpiringContract[];
  renewalPipeline: ContractRenewal[];
  financialAdjustmentProposals: ContractRenewal[];
}

/** Espelha CreateContractRenewalDto. */
export interface RenewalInput {
  vendorName?: string;
  indexName?: string;
  currentValue: number;
  proposedValue: number;
  effectiveDate?: string;
  notes?: string;
}

/* ── Regras de alçada ── */

export const APPROVAL_CONDITIONS = ['ALWAYS', 'VALUE_GT', 'VALUE_BETWEEN'] as const;
export type ApprovalCondition = (typeof APPROVAL_CONDITIONS)[number];

/** Espelha ApprovalRuleDto. */
export interface ApprovalRule {
  id: string;
  documentType: string;
  costCenter: string | null;
  condition: string;
  minValue: number | null;
  maxValue: number | null;
  approvalLevel: number;
  roleTitle: string;
  financialLimit: number | null;
  active: boolean;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Espelha CreateApprovalRuleDto / UpdateApprovalRuleDto. */
export interface ApprovalRuleInput {
  documentType: string;
  costCenter?: string | null;
  condition: ApprovalCondition;
  minValue?: number | null;
  maxValue?: number | null;
  approvalLevel: number;
  roleTitle: string;
  financialLimit?: number | null;
  active?: boolean;
  description?: string;
}

/** Espelha UpdateApprovalRuleDto (PATCH — todos os campos opcionais). */
export interface ApprovalRuleUpdateInput {
  documentType?: string;
  costCenter?: string | null;
  condition?: ApprovalCondition;
  minValue?: number | null;
  maxValue?: number | null;
  approvalLevel?: number;
  roleTitle?: string;
  financialLimit?: number | null;
  active?: boolean;
  description?: string;
}

/** Espelha ResolvedApprovalRuleDto (resultado de /approval-rules/resolve). */
export interface ResolvedApprovalRule {
  ruleId: string;
  documentType: string;
  costCenter: string | null;
  condition: string;
  minValue: number | null;
  maxValue: number | null;
  approvalLevel: number;
  roleTitle: string;
  financialLimit: number | null;
}

/** Espelha ResolveApprovalRuleResultDto. */
export interface ResolveApprovalResult {
  rules: ResolvedApprovalRule[];
  requiredLevels: number;
  requiresApproval: boolean;
}

/* ── Dashboard de integração contrato-financeiro ── */

/** Espelha UnbilledByContractDto. */
export interface UnbilledByContract {
  contractId: string;
  clientId: string;
  value: number;
  status: string;
  unbilledCount: number;
  unbilledAmount: number;
  readyCount: number;
  readyAmount: number;
  billedAmount: number;
  paidAmount: number;
}

/** Espelha PendingFinancialSetupDto. */
export interface PendingFinancialSetup {
  contractId: string;
  clientId: string;
  clientName: string | null;
  value: number;
  status: string;
  startDate: string;
}

/**
 * Espelha ContractIntegrationDashboardDto.
 * reconciliationRate vem em escala 0–100 (ex.: 98.2 = 98,2%).
 */
export interface ContractFinanceDashboard {
  unbilledRevenue: number;
  unbilledByContract: UnbilledByContract[];
  readyForBilling: { count: number; amount: number; contracts: number };
  reconciliationRate: number;
  reconciliation: {
    scheduledCount: number;
    scheduledAmount: number;
    billedCount: number;
    billedAmount: number;
    paidCount: number;
    paidAmount: number;
  };
  pendingFinancialSetup: PendingFinancialSetup[];
}